import { createSignal, onCleanup, createMemo } from "solid-js";
import type { RealtimeConnectionInfo, RealtimeStats } from "../types";
import { matchesDebugFilter } from "@searchgrid/shared/realtime/filter";
import { buildControlPlaneHeaders, getAdminRuntimeConfig } from "../runtime";

export interface DebugFilterState {
  projectIds: string[];
  connectionIds: string[];
  roles: string[];
  eventTypes: string[];
  userIds: string[];
}

export function createDefaultFilter(): DebugFilterState {
  return {
    projectIds: [],
    connectionIds: [],
    roles: [],
    eventTypes: [],
    userIds: [],
  };
}

export function useDebugWebSocket() {
  const [allEvents, setAllEvents] = createSignal<any[]>([]);
  const [connections, setConnections] = createSignal<RealtimeConnectionInfo[]>([]);
  const [stats, setStats] = createSignal<RealtimeStats | null>(null);
  const [connected, setConnected] = createSignal(false);
  const [error, setError] = createSignal("");
  const [clientFilters, setClientFilters] = createSignal<DebugFilterState>(createDefaultFilter());
  const [eventLimit] = createSignal(200);

  let ws: WebSocket | null = null;
  let serverFilters: DebugFilterState = createDefaultFilter();

  const events = createMemo(() => {
    const f = clientFilters();
    if (f.projectIds.length === 0 && f.connectionIds.length === 0 &&
        f.roles.length === 0 && f.eventTypes.length === 0 && f.userIds.length === 0) {
      return allEvents();
    }
    return allEvents().filter((msg: any) => matchesDebugFilter(msg, f));
  });

  async function connect(filters?: Partial<DebugFilterState>, persist = false) {
    disconnect();

    const initial = { ...createDefaultFilter(), ...(filters ?? {}) };
    serverFilters = initial;
    setClientFilters(initial);

    let token = "";
    try {
      const res = await fetch("/api/ws/debug-token", {
        headers: buildControlPlaneHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      token = data.token;
      if (!token) throw new Error("No token returned");
    } catch (e: any) {
      setError(`Failed to get debug token: ${e.message}`);
      return;
    }

    const managedBaseUrl = getAdminRuntimeConfig().managedAppBaseUrl.replace(/\/+$/, "");
    const wsBaseUrl = managedBaseUrl
      .replace(/^http:\/\//i, "ws://")
      .replace(/^https:\/\//i, "wss://")
      .replace(/\/api\/v1$/i, "");
    const params = new URLSearchParams();
    if (token) params.set("token", token);
    if (initial.roles.length) params.set("roles", initial.roles.join(","));
    if (initial.eventTypes.length) params.set("types", initial.eventTypes.join(","));
    if (persist) params.set("persist", "1");
    const url = `${wsBaseUrl}/api/v1/realtime/debug?${params.toString()}`;

    try {
      ws = new WebSocket(url);
      ws.onopen = () => {
        setConnected(true);
        setError("");
        sendFilter();
        ws?.send(JSON.stringify({ type: "debug:get-connections" }));
        ws?.send(JSON.stringify({ type: "debug:get-stats" }));
      };
      ws.onclose = (e) => {
        setConnected(false);
        ws = null;
        if (!e.wasClean) {
          setError("WebSocket closed unexpectedly. Check API server and JWT secret.");
        }
      };
      ws.onerror = () => {
        setConnected(false);
        setError("WebSocket connection failed. Is the API server running?");
      };
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === "debug:connections") {
            setConnections(msg.connections ?? []);
          } else if (msg.type === "debug:stats") {
            setStats(msg.stats ?? null);
          } else if (msg.type === "debug:message" || msg.original) {
            setAllEvents((prev) => [msg, ...prev].slice(0, eventLimit()));
          }
        } catch {}
      };
    } catch (e: any) {
      setError(`WebSocket error: ${e.message}`);
      setConnected(false);
    }
  }

  function disconnect() {
    if (ws) {
      ws.onclose = null;
      ws.onerror = null;
      ws.onmessage = null;
      ws.close();
      ws = null;
    }
    setConnected(false);
    setAllEvents([]);
  }

  function sendFilter() {
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({
        type: "debug:filter",
        filters: serverFilters,
      }));
    }
  }

  function updateFilters(filters: Partial<DebugFilterState>) {
    serverFilters = { ...serverFilters, ...filters };
    setClientFilters(serverFilters);
    sendFilter();
  }

  function setConnectionFilter(connectionIds: string[]) {
    updateFilters({ connectionIds });
  }

  function setProjectFilter(projectIds: string[]) {
    updateFilters({ projectIds });
  }

  function requestConnections() {
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "debug:get-connections" }));
    }
  }

  function requestStats() {
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "debug:get-stats" }));
    }
  }

  function clearEvents() {
    setAllEvents([]);
  }

  function clearError() {
    setError("");
  }

  onCleanup(disconnect);

  return {
    events,
    allEvents,
    connections,
    stats,
    connected,
    error,
    clientFilters,
    connect,
    disconnect,
    updateFilters,
    setConnectionFilter,
    setProjectFilter,
    requestConnections,
    requestStats,
    clearEvents,
    clearError,
  };
}
